import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { __, sprintf } from '@wordpress/i18n';
import useFetch from '../../Hooks/useFetch';

import './style.scss';

const UninstallSurvey = ({ data }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [selectedReason, setSelectedReason] = useState('');
	const [details, setDetails] = useState('');
	const [isAnonymous, setIsAnonymous] = useState(false);
	const [redirectLink, setRedirectLink] = useState('');

	const { id, slug, name, type, reasons, contact_url, nonces, ajax_url } = data;
	const { request, isLoading, error } = useFetch(ajax_url);

	// Listen for the deactivation click
	useEffect(() => {
		const handleDeactivationClick = (e) => {
			// Find the closest anchor that looks like a deactivation link
			const deactivateLink = e.target.closest('#the-list .deactivate a');
			if (!deactivateLink) return;

			// CRITICAL: Ensure we aren't accidentally matching our own Opt-In link
			// (which might be unshifted into the list and wrapped in a span by another plugin)
			if (e.target.closest('.fs-opt-in-popup-trigger')) return;

			const row = deactivateLink.closest('tr');
			if (!row) return;

			// Verify this row belongs to our plugin
			const rowSlug = row.dataset.slug;
			const marker = row.querySelector(`.fs-module-id[data-module-id="${id}"]`);

			if (rowSlug === slug || marker) {
				e.preventDefault();
				console.log(`FS-Lite [${slug}]: Deactivation detected`);
				setRedirectLink(deactivateLink.getAttribute('href'));
				setIsVisible(true);
			}
		};

		document.addEventListener('click', handleDeactivationClick);
		return () => document.removeEventListener('click', handleDeactivationClick);
	}, [slug, id]);

	const handleReasonChange = (reasonId) => {
		setSelectedReason(reasonId);
		setDetails(''); // Reset details when reason changes
	};

	const handleSubmit = async (e) => {
		if (e) e.preventDefault();

		// If no reason selected, it's a "Skip & Deactivate"
		if (selectedReason) {
			await request(`fs_uninstall_reason_${id}`, nonces.uninstall, {
				reason_id: selectedReason,
				details,
				is_anonymous: isAnonymous ? '1' : '0',
			});
		}

		// Always redirect to the captured link
		window.location.href = redirectLink;
	};

	const getSubmitButtonLabel = () => {
		if (isLoading) return __('Processing...', 'pev-fs-lite');

		const deactivateText = type === 'plugin' ? __('Deactivate', 'pev-fs-lite') : __('Activate Theme', 'pev-fs-lite');

		if (!selectedReason) {
			return sprintf(__('Skip & %s', 'pev-fs-lite'), deactivateText);
		}

		const reason = reasons.find(r => r.id === selectedReason);
		if (reason?.id === '7' && !details.trim()) { // Other reason requires input for "Submit" label
			return deactivateText;
		}

		return sprintf(__('Submit & %s', 'pev-fs-lite'), deactivateText);
	};

	if (!isVisible) return null;

	return createPortal(
		<div className="pevFSLite pev-fs-modal fs-modal-deactivation-feedback">
			<div className="pev-fs-modal-dialog">
				<div className="fs-modal-header">
					<h4>{__('Quick Feedback', 'pev-fs-lite')}</h4>
				</div>
				<div className="fs-modal-body">
					{error && <div className="fs-error-message" style={{ color: '#d63638', marginBottom: '15px' }}>{error}</div>}
					<div className="fs-modal-panel active" data-panel-id="reasons">
						<h3><strong>{sprintf(__('If you have a moment, please let us know why you are deactivating %s:', 'pev-fs-lite'), name)}</strong></h3>
						<ul id="reasons-list">
							{reasons.map((reason) => (
								<li key={reason.id} className={`reason ${reason.input_type ? 'has-input' : ''}`}>
									<label>
										<span>
											<input
												type="radio"
												name="selected-reason"
												value={reason.id}
												checked={selectedReason === reason.id}
												onChange={() => handleReasonChange(reason.id)}
											/>
										</span>
										<span>{reason.label}</span>
									</label>
									{selectedReason === reason.id && reason.internal_message === 'support' && (
										<div className="internal-message">
											{__('Sorry for the inconvenience and we are here to help if you give us a chance.', 'pev-fs-lite')}
											{' '}
											<a href={contact_url} target="_blank" rel="noopener noreferrer" className="button button-small button-primary">
												{__('Contact Support', 'pev-fs-lite')}
											</a>
										</div>
									)}
									{selectedReason === reason.id && reason.input_type && (
										<div className="reason-input">
											{reason.input_type === 'textfield' ? (
												<input
													type="text"
													placeholder={reason.input_placeholder}
													value={details}
													onChange={(e) => setDetails(e.target.value)}
													autoFocus
												/>
											) : (
												<textarea
													placeholder={reason.input_placeholder}
													value={details}
													onChange={(e) => setDetails(e.target.value)}
													autoFocus
												/>
											)}
										</div>
									)}
								</li>
							))}
						</ul>
					</div>
				</div>
				<div className="fs-modal-footer">
					<label className="anonymous-feedback-label">
						<input
							type="checkbox"
							className="anonymous-feedback-checkbox"
							checked={isAnonymous}
							onChange={(e) => setIsAnonymous(e.target.checked)}
						/> {__('Anonymous feedback', 'pev-fs-lite')}
					</label>
					<button
						className={`button button-primary button-deactivate ${isLoading ? 'disabled' : ''}`}
						onClick={handleSubmit}
						disabled={isLoading}
					>
						{getSubmitButtonLabel()}
					</button>
					<button
						className="button button-secondary button-close"
						onClick={() => setIsVisible(false)}
						disabled={isLoading}
					>
						{__('Cancel', 'pev-fs-lite')}
					</button>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default UninstallSurvey;
